import { useCallback, useEffect, useState, useRef, Key as ReactKey } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { ActivityGameMatchInfo, ActivityOutletContext } from '../../interface';
import { getMatchBracket, batchUpdateActivityMatchCourt } from '../../apiBridge';
import {Button, Form, Input, Modal, Select, Table, message, InputRef, Space} from 'antd';
import { EditOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import EditMatchModal from './_editMatchModal';
import MatchResultModal from './_matchResultModal';
import StateTag from './_stateTag';
import Link from 'antd/es/typography/Link';
import { isMatchWinner, isReferencePreviewMatch, matchParticipantDisplayName, REFERENCE_PREVIEW_HINT } from './activityMatch';


const ActivityMatches: React.FC = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const data = useOutletContext() as ActivityOutletContext

    const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const categoryFromUrl = searchParams.get('category');

    const [nowCategory, setNowCategory] = useState<string | null>(categoryFromUrl);
    const [nowRound, setNowRound] = useState<number>(-2);
    const [nowCourt, setNowCourt] = useState<number | null>(null);
    const [nowPlanDate, setNowPlanDate] = useState<string | null>(null);
    const [loadingState, setLoadingState] = useState<boolean>(true);
    const [matchData, setMatchData] = useState<ActivityGameMatchInfo[]>([]);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [currentMatch, setCurrentMatch] = useState<ActivityGameMatchInfo | null>(null);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [globalSearchText, setGlobalSearchText] = useState('');
    const [isResultModalVisible, setIsResultModalVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<ReactKey[]>([]);
    const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
    const [selectedCourt, setSelectedCourt] = useState<number | null>(null);

    const handleCategoryChange = (value: string) => {
        setMatchData([]);
        setLoadingState(true);
        setNowCategory(value);
        setNowRound(-2);
        setNowCourt(null);

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('category', value);
        window.history.pushState({}, '', `?${newSearchParams.toString()}`);
    }

    const handleRoundChange = (value: number) => {
        setNowRound(value);
    }

    const handleCourtChange = (value: number | null) => {
        setNowCourt(value);
    }

    const handlePlanDateChange = (value: string | null) => {
        setNowPlanDate(value);
    }

    const handleViewBracket = () => {
        if (!nowCategory) return;
        navigate(`/activity/${data.activity.id}/bracket?category=${nowCategory}`);
    };

    const loadMatches = useCallback(async (categoryId: string | null, ignore?: () => boolean) => {
        const skipped = ignore ?? (() => false);
        if (!categoryId) {
            if (!skipped()) {
                setMatchData([]);
                setLoadingState(false);
            }
            return;
        }

        setLoadingState(true);
        try {
            const response = await getMatchBracket(data.activity.id, categoryId);
            if (skipped()) {
                return;
            }
            const res = await response.json();
            if (skipped()) {
                return;
            }
            if (res.error) {
                setMatchData([]);
                messageApi.error(res.error);
                return;
            }
            const nextMatches = Array.isArray(res.matches) ? res.matches : [];
            setMatchData(nextMatches);
            if (nextMatches.length === 0) {
                messageApi.info('No matches found');
            }
        } catch {
            if (!skipped()) {
                setMatchData([]);
                messageApi.error('Failed to load matches');
            }
        } finally {
            if (!skipped()) {
                setLoadingState(false);
            }
        }
    }, [data.activity.id, messageApi]);

    useEffect(() => {
        let cancelled = false;
        loadMatches(nowCategory, () => cancelled);
        return () => {
            cancelled = true;
        };
    }, [nowCategory, loadMatches]);

    const warnReferencePreview = () => {
        messageApi.info(REFERENCE_PREVIEW_HINT);
    };

    const handleEdit = (record: ActivityGameMatchInfo) => {
        if (isReferencePreviewMatch(record)) {
            warnReferencePreview();
            return;
        }
        setCurrentMatch(record);
        setIsEditModalVisible(true);
    };

    const handleMatchReset = () => {
        loadMatches(nowCategory);
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: string,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const handleRoundResult = async (record: ActivityGameMatchInfo) => {
        if (isReferencePreviewMatch(record)) {
            warnReferencePreview();
            return;
        }
        setCurrentMatch(record);
        setIsResultModalVisible(true);
    }

    // 高亮文本函数
    const highlightText = (text: string, search: string) => {
        if (!search) return text;
        const parts = text.split(new RegExp(`(${search})`, 'gi'));
        return parts.map((part, i) => 
            part.toLowerCase() === search.toLowerCase() ? 
            <span key={i} style={{ backgroundColor: '#ffc069' }}>{part}</span> : 
            part
        );
    };

    const getColumnSearchProps = (dataIndex: string): ColumnType<ActivityGameMatchInfo> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex === 'player1' ? 'Player 1' : 'Player 2'}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value: boolean | ReactKey, record: ActivityGameMatchInfo) => {
            const participant = record.participants[dataIndex === 'player1' ? 0 : 1];
            return participant?.display_name
                ? participant.display_name.toLowerCase().includes(String(value).toLowerCase())
                : false;
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text: string, record: ActivityGameMatchInfo) => {
            const participant = record.participants[dataIndex === 'player1' ? 0 : 1];
            const displayName = matchParticipantDisplayName(participant, record.state);
            const columnSearch = searchedColumn === dataIndex ? searchText : '';
            const highlightedText = (globalSearchText || columnSearch)
                ? highlightText(displayName, globalSearchText || columnSearch)
                : displayName;

            if (isMatchWinner(record, participant?.id)) {
                return <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>{highlightedText}</span>;
            }

            return highlightedText;
        },
    });

    // 表格列定义
    const columns: ColumnType<ActivityGameMatchInfo>[] = [
        {
            title: 'Bout Number',
            dataIndex: 'bout_number',
            key: 'bout_number',
            render: (text: number) => {
                if (globalSearchText) {
                    return highlightText(text.toString(), globalSearchText);
                }
                return text > 0 ? text : '-';
            },
        },
        {
            title: 'Round',
            dataIndex: 'round',
            key: 'round',
            render: (round: number, record: ActivityGameMatchInfo) => {
                if (!data.categories || !nowCategory) return round;
                const category = data.categories.find(cat => cat.id.toString() === nowCategory);
                if (!category) return round;
                // 处理普通轮次
                const roundNames = category.round_names;
                
                if (round >= 0 && round < roundNames.length) {
                    return roundNames[round];
                }
                
                return round;
            },
        },
        {
            title: 'Court',
            dataIndex: 'court',
            key: 'court',
            render: (court: number) => court === 0 ? 'Not Specified' : court,
        },
        {
            title: 'State',
            dataIndex: 'state',
            key: 'state',
            filters: [
                { text: 'Wait', value: 'wait' },
                { text: 'In Game', value: 'in_game' },
                { text: 'Finish', value: 'finish' },
                { text: 'Locked', value: 'locked' },
                { text: 'Walkover', value: 'walkover' },
                { text: 'Bye', value: 'bye' },
            ],
            onFilter: (value: boolean | ReactKey, record: ActivityGameMatchInfo) => record.state === String(value),
            render: (state: string) => <StateTag state={state} />,
        },
        {
            title: 'Player 1 (Blue)',
            key: 'player1',
            ...getColumnSearchProps('player1'),
        },
        {
            title: 'Player 2 (Red)',
            key: 'player2',
            ...getColumnSearchProps('player2'),
        },
        {
            title: 'Action',
            key: 'action',
            render: (record: ActivityGameMatchInfo) => (
                <Space>
                    <Link onClick={() => handleEdit(record)}><EditOutlined /> Edit</Link>
                    <Link onClick={() => handleRoundResult(record)}>Result</Link>
                </Space>
            ),
        },
    ];

    const getRoundOptions = () => {
        if (!nowCategory || !data.categories) return [];
        const category = data.categories.find(cat => cat.id.toString() === nowCategory);
        if (!category) return [];
        
        const options = [
            {
                label: 'All',
                value: -2
            }
        ];
        

        const roundNames = category.round_names;
        roundNames.forEach((name, index) => {
            options.push({
                label: name,
                value: index
            });
        });

        return options;
    };

    const getCourtOptions = () => {
        const courts = new Set<number>();
        matchData.forEach(match => {
            if (match.court) {
                courts.add(match.court);
            }
        });
        return Array.from(courts).sort((a, b) => a - b).map(court => ({
            label: `Court ${court}`,
            value: court
        }));
    };

    const getPlanDateOptions = () => {
        const dates = new Set<string>();
        matchData.forEach(match => {
            if (match.plan_date) {
                dates.add(match.plan_date);
            }
        });
        return Array.from(dates).sort().map(date => ({
            label: date,
            value: date
        }));
    };

    const handleGlobalSearch = (value: string) => {
        setGlobalSearchText(value);
    };

    const filteredMatches = matchData.filter(match => {
        // 先处理轮次过滤
        if (nowRound !== null && nowRound !== -2 && match.round !== nowRound) {
            return false;
        }

        // 处理场地过滤
        if (nowCourt !== null && match.court !== nowCourt) {
            return false;
        }

        if (nowPlanDate !== null && match.plan_date !== nowPlanDate) {
            return false;
        }

        if (globalSearchText) {
            const searchText = globalSearchText.toLowerCase();
            if (match.bout_number?.toString().includes(searchText)) {
                return true;
            }

            if (match.id === searchText) {
                return true
            }

            const hasMatchingPlayer = match.participants.some(participant => 
                participant?.display_name?.toLowerCase().includes(searchText)
            );
            if (hasMatchingPlayer) {
                return true;
            }
            return false;
        }

        return true;
    });

    const handleBatchMove = async () => {
        if (!selectedCourt) {
            messageApi.error('Please select a court');
            return;
        }

        try {
            const response = await batchUpdateActivityMatchCourt(
                data.activity.id,
                selectedRowKeys.map(key => key.toString()),
                selectedCourt
            );
            const result = await response.json();
            
            if (result.error) {
                messageApi.error(result.error);
            } else {
                messageApi.success('Successfully moved matches');
                setIsMoveModalVisible(false);
                setSelectedRowKeys([]);
                handleMatchReset();
            }
        } catch (error) {
            messageApi.error('Failed to move matches');
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
        getCheckboxProps: (record: ActivityGameMatchInfo) => ({
            disabled:
                isReferencePreviewMatch(record) ||
                (record.state !== 'wait' && record.state !== 'locked') ||
                record.court === 0,
        }),
    };

    return (
        <>
            {contextHolder}
            <div style={{ marginBottom: 24 }}>
                <div style={{ 
                    background: '#fff', 
                    padding: '24px', 
                    borderRadius: '8px',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)'
                }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '16px',
                            alignItems: 'flex-start'
                        }}>
                            <Form.Item 
                                label="Category" 
                                style={{ marginBottom: 0 }}
                            >
                                <Select
                                    style={{ width: 200 }}
                                    onChange={handleCategoryChange}
                                    value={nowCategory}
                                    options={data.categories?.map(category => ({
                                        label: category.name,
                                        value: category.id.toString(),
                                        key: category.id.toString()
                                    }))}
                                    placeholder="Select a category"
                                    loading={!data.categories}
                                />
                            </Form.Item>
                            {nowCategory && (
                                <>
                                    <Form.Item 
                                        label="Round" 
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Select
                                            style={{ width: 200 }}
                                            onChange={handleRoundChange}
                                            value={nowRound}
                                            options={getRoundOptions()}
                                            loading={!data.categories || !matchData}
                                        />
                                    </Form.Item>
                                    <Form.Item 
                                        label="Court" 
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Select
                                            style={{ width: 200 }}
                                            onChange={handleCourtChange}
                                            value={nowCourt}
                                            options={[
                                                { label: 'All Courts', value: null },
                                                ...getCourtOptions()
                                            ]}
                                        />
                                    </Form.Item>
                                    <Form.Item 
                                        label="Plan Date" 
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Select
                                            style={{ width: 200 }}
                                            onChange={handlePlanDateChange}
                                            value={nowPlanDate}
                                            options={[
                                                { label: 'All Dates', value: null },
                                                ...getPlanDateOptions()
                                            ]}
                                        />
                                    </Form.Item>
                                    <Button 
                                        type="primary" 
                                        onClick={handleViewBracket}
                                        style={{ marginTop: 4 }}
                                    >
                                        View Bracket
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={() => setIsMoveModalVisible(true)}
                                        disabled={selectedRowKeys.length === 0}
                                        style={{ marginTop: 4 }}
                                    >
                                        Batch Move
                                    </Button>
                                </>
                            )}
                        </div>
                        <div style={{ marginTop: 16 }}>
                            <Input.Search
                                placeholder="Search bout number or player"
                                allowClear
                                onChange={(e) => handleGlobalSearch(e.target.value)}
                                style={{ width: 300 }}
                                enterButton
                            />
                        </div>
                    </Space>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={filteredMatches}
                rowKey="id"
                loading={loadingState}
                rowSelection={rowSelection}
                scroll={{
                    x: "max-content",
                }}
            />

            <EditMatchModal
                visible={isEditModalVisible}
                onCancel={() => setIsEditModalVisible(false)}
                onOk={() => setIsEditModalVisible(false)}
                currentMatch={currentMatch}
                onMatchReset={handleMatchReset}
                matchData={matchData}
            />

            <MatchResultModal
                visible={isResultModalVisible}
                onCancel={() => setIsResultModalVisible(false)}
                currentMatch={currentMatch}
                activityId={data.activity.id}
            />

            <Modal
                title="Move Matches"
                open={isMoveModalVisible}
                onOk={handleBatchMove}
                onCancel={() => {
                    setIsMoveModalVisible(false);
                    setSelectedCourt(null);
                }}
                okText="Move"
                cancelText="Cancel"
            >
                <Form layout="vertical">
                    <Form.Item
                        label="Select Court"
                        required
                        validateStatus={selectedCourt === null ? 'error' : ''}
                        help={selectedCourt === null ? 'Please select a court' : ''}
                    >
                        <Select
                            style={{ width: '100%' }}
                            onChange={(value) => setSelectedCourt(value)}
                            value={selectedCourt}
                            options={[
                                ...Array.from({ length: data.activity.max_court_count }, (_, i) => ({
                                    label: `Court ${i + 1}`,
                                    value: i + 1
                                }))
                            ]}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ActivityMatches;
